import { gql } from '@apollo/client'
import { useActionMutation, type UseMutationProps } from '@ztechapps/common.hook'
import type { Adjustment, StateTransitionArgs } from '@ztechapps/common.contract'
import { AdjustmentStatusEnum } from '@ztechapps/common.contract'
import { adjustmentFragment } from './graphql'

/**
 * Mutation to change adjustment status using the generic state transition.
 * Allows transitioning to any valid state.
 */
export const changeAdjustmentStatusQuery = gql`
  mutation changeAdjustmentStatus($input: StateTransitionArgs!) {
    changeAdjustmentStatus(input: $input) {
      ...adjustmentFragment
    }
  }
  ${adjustmentFragment}
`

/**
 * Hook for managing adjustment state transitions.
 * Provides convenient wrapper functions that all use the generic changeAdjustmentStatus mutation.
 *
 * @example
 * ```tsx
 * const [{ moveToInProgress, moveToComplete }, { loading, error }] = useAdjustmentStateTransition()
 *
 * // Move to In Progress by _id
 * const result = await moveToInProgress(undefined, adjustmentId)
 *
 * // Move to Complete by filter
 * const result = await moveToComplete({ _id: adjustmentId })
 * ```
 */
export const useAdjustmentStateTransition = ({ query, ...props } = {} as UseMutationProps) => {
  query = query ?? changeAdjustmentStatusQuery

  const [_changeStatus, result] = useActionMutation<Adjustment, StateTransitionArgs<Adjustment>>({
    query,
    ...props,
  })

  const changeStatus = (targetState: AdjustmentStatusEnum, adjustmentId: string) => {
    const filter = { _id: adjustmentId }
    return _changeStatus({ targetState, filter }, filter, adjustmentId)
  }

  /**
   * Moves adjustment to In Progress status.
   */
  const moveToInProgress = (_id: string) => {
    return changeStatus(AdjustmentStatusEnum.InProgress, _id)
  }

  /**
   * Moves adjustment to Complete status.
   * This finalizes the adjustment and updates inventory.
   */
  const moveToComplete = (_id: string) => {
    return changeStatus(AdjustmentStatusEnum.Complete, _id)
  }

  /**
   * Moves adjustment to Cancelled status.
   */
  const moveToCancel = (_id?: string) => {
    return changeStatus(AdjustmentStatusEnum.Cancelled, _id)
  }

  /**
   * Moves adjustment back to Draft status.
   */
  const moveToDraft = (_id?: string) => {
    return changeStatus(AdjustmentStatusEnum.Draft, _id)
  }

  return [
    {
      // Generic mutation function
      changeStatus,

      // Convenience wrapper functions
      moveToInProgress,
      moveToComplete,
      moveToCancel,
      moveToDraft,
    },
    result,
  ] as const
}
